import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseSortOrder, DatasourceUpstreamDatabasesQueryVariables, DatasourceUpstreamDatabasesQuery, DatabasesDisplayGridRowDataFragment, UpstreamDatabasesFilteredByFieldQueryVariables, UpstreamDatabasesFilteredByFieldQuery , DatasourceUpstreamDatabasesDocument , UpstreamDatabasesFilteredByFieldDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper
} from '../../../data-catalog-shared';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';

type QueryVariables = DatasourceUpstreamDatabasesQueryVariables;
type QueryResults = DatasourceUpstreamDatabasesQuery;
type QueryResultListNode = DatabasesDisplayGridRowDataFragment;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = UpstreamDatabasesFilteredByFieldQueryVariables;
type QueryResultsWithFilter = UpstreamDatabasesFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].upstreamDatabasesConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].upstreamDatabasesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes && resultData.fieldsConnection.nodes[0];
}

function getRowDataForDatabase(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatasourceUpstreamDatabasesQueryGridProps =
  GridSortProps<DatabasesValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceUpstreamDatabasesQueryGrid(props: DatasourceUpstreamDatabasesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyUpstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatabasesLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={UpstreamDatabasesFilteredByFieldDocument}
      partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as DatabaseSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={DatasourceUpstreamDatabasesDocument}
      partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as DatabaseSortOrder }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForDatabase}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
